import { isExceptionFlowForUrologyOrg } from "../../../../enums/organizationTypes";
import {
  createFilterForBoStatusForAlgolia,
  createFilterForCreatedAtForAlgolia,
  createFilterForDateOfServiceForAlgolia,
  createFilterForDateOfWorkForAlgolia,
  createFilterForDocumentUploadStatusForAlgolia,
  createFilterForEstimateValueForAlgolia,
  createFilterForEvWriteBackStatusForAlgolia,
  createFilterForRegimenNamesFromAlgoliaMultiFacetsCount,
  createFilterOptionsBasedOnAuthStatusForAlgolia,
  createFilterOptionsBasedOnLocationFromAlgoliaMultiFacetsCount,
  createFilterOptionsBasedOnPractitionerNameFromAlgoliaMultiFacetsCount,
  createFilterOptionsForEvPrimary,
  createFilterOptionsForOrderStateStatus,
  createFilterOptionsForPlanDetailsFromAlgoliaMultiFacetsCount,
  createFilterOptionsForRegimenType,
  createFilterOptionsForSecondaryPayerName,
} from "../../medicalPaOrdersAlgolia/utils/filterOptionsGenrator";

/**
 * Configuration for filter creation functions
 * Defines the order and type of filters to be created
 */
export interface FilterCreationConfig {
  id: string;
  createFunction: (data?: any) => any;
  dataExtractor?: (algoliaData: any) => any;
  requiresData: boolean;
}

/**
 * BO Status filter entry used for every org outside `exceptionFlowForUrologyOrgs`.
 * Reuses the shared BO options and builds a section keyed `bo_status`.
 */
const BO_STATUS_FILTER_CONFIG: FilterCreationConfig = {
  id: "boStatus",
  createFunction: createFilterForBoStatusForAlgolia,
  requiresData: true,
};

/**
 * Estimate Value filter entry used for orgs in `exceptionFlowForUrologyOrgs`
 * in place of BO Status. Options come from Firestore `estimate_options` and the
 * section is keyed `estimate_value`, so the query filters on that field.
 */
const ESTIMATE_VALUE_FILTER_CONFIG: FilterCreationConfig = {
  id: "estimateValue",
  createFunction: createFilterForEstimateValueForAlgolia,
  requiresData: true,
};

/**
 * Builds the filter creation config for the My PA Orders worklist.
 *
 * For orgs in `exceptionFlowForUrologyOrgs` the BO Status filter is replaced
 * by an Estimate Value filter (keyed `estimate_value`, options from
 * `estimateOptions` Redux); every other org keeps the BO Status filter. All other
 * filters are identical across orgs.
 *
 * @param orgId - Organization/facility ID for the current session
 * @returns Ordered filter creation config for the current org
 */
export const getFilterCreationConfigForMyPaOrders = (
  orgId?: string,
): FilterCreationConfig[] => {
  const isSunstateUrology = isExceptionFlowForUrologyOrg(orgId);

  return [
    {
      id: "dateOfService",
      createFunction: createFilterForDateOfServiceForAlgolia,
      requiresData: false,
    },
    {
      id: "createdAt",
      createFunction: createFilterForCreatedAtForAlgolia,
      requiresData: false,
    },
    {
      id: "dateOfWork",
      createFunction: createFilterForDateOfWorkForAlgolia,
      requiresData: false,
    },
    {
      id: "regimenNames",
      createFunction: createFilterForRegimenNamesFromAlgoliaMultiFacetsCount,
      dataExtractor: (algoliaData) =>
        Object.keys(algoliaData?.facet_results?.regimen_name ?? {}),
      requiresData: true,
    },
    isSunstateUrology ? ESTIMATE_VALUE_FILTER_CONFIG : BO_STATUS_FILTER_CONFIG,
    {
      id: "authStatus",
      createFunction: createFilterOptionsBasedOnAuthStatusForAlgolia,
      requiresData: true,
    },
    {
      id: "evWriteBackStatus",
      createFunction: createFilterForEvWriteBackStatusForAlgolia,
      requiresData: false,
    },
    {
      id: "documentUploadStatus",
      createFunction: createFilterForDocumentUploadStatusForAlgolia,
      requiresData: false,
    },
    {
      id: "planDetails",
      createFunction:
        createFilterOptionsForPlanDetailsFromAlgoliaMultiFacetsCount,
      dataExtractor: (algoliaData) =>
        Object.keys(algoliaData?.facet_results?.primary_payer_name ?? {}),
      requiresData: true,
    },
    {
      id: "secondaryPayerName",
      createFunction: createFilterOptionsForSecondaryPayerName,
      dataExtractor: (algoliaData) =>
        Object.keys(algoliaData?.facet_results?.secondary_payer_name ?? {}),
      requiresData: true,
    },
    {
      id: "practitionerName",
      createFunction:
        createFilterOptionsBasedOnPractitionerNameFromAlgoliaMultiFacetsCount,
      dataExtractor: (algoliaData) =>
        Object.keys(algoliaData?.facet_results?.practitioner_name ?? {}),
      requiresData: true,
    },
    {
      id: "location",
      createFunction:
        createFilterOptionsBasedOnLocationFromAlgoliaMultiFacetsCount,
      dataExtractor: (algoliaData) =>
        Object.keys(algoliaData?.facet_results?.location ?? {}),
      requiresData: true,
    },
    {
      id: "evPrimary",
      createFunction: createFilterOptionsForEvPrimary,
      dataExtractor: (algoliaData) =>
        Object.keys(algoliaData?.facet_results?.ev_bv_primary ?? {}),
      requiresData: true,
    },
    {
      id: "regimenType",
      createFunction: createFilterOptionsForRegimenType,
      requiresData: false,
    },
    {
      id: "orderStateStatus",
      createFunction: createFilterOptionsForOrderStateStatus,
      requiresData: false,
    },
  ];
};
